import React, { useEffect, useState } from 'react';
import { SectionLabel } from '../components/primitives.js';
import {
  canopusCommandService,
  type CanopusSIPeriod,
  type CanopusTrendsResult,
  type CanopusTrendsMonthlyEntry,
  type CanopusTrendsSpecialtyVariance,
  type CanopusTrendsSurgeonProcedureVariance,
  type CanopusTrendsVarianceHeatmap,
  type CanopusTrendsAdoptionBucket,
  type CanopusTrendsSurgeonDurationIntel,
  type CanopusTrendsRoomGap,
  type CanopusTrendsKpis,
} from '../../../services/canopusCommand.js';
// synthetic "Avg Team TFI" overlay for the monthly trends.
// SI overlay — replaces zero siTotal/siOffCase/siPerCase per
// month with deterministic plausible values while preserving the real cases
// / avgDur / variance numbers.
import type { InsightsSurgeon } from '../../../services/insights.js';
import {
  getDemoMonthlySIOverlay,
  getDemoMonthlyTfiOverlay,
  getDemoTrends,
} from '../data/demo-data.js';
import './trends.css';

type TrendsView = 'trends' | 'variance' | 'duration';
type VarianceView = 'overview' | 'surgeon' | 'timeline';

// MonthlyMetricKey includes 'avgTfi' which is NOT part of the
// backend `CanopusTrendsMonthlyEntry` type. It's injected client-side only in
// demo mode; in live mode the value is null and the chart renders '--'.
type MonthlyMetricKey =
  | keyof Pick<
      CanopusTrendsMonthlyEntry,
      | 'cases'
      | 'avgDurMin'
      | 'avgVarianceMin'
      | 'longRatePct'
      | 'shortRatePct'
      | 'runtimeAboveMin'
      | 'runtimeBelowMin'
      | 'siTotal'
      | 'siOffCase'
      | 'siPerCase'
    >
  | 'avgTfi';

// Local row type that extends the backend monthly entry with
// a synthetic `avgTfi` field used by the "Avg Team TFI" MiniChart.
type MonthlyEntryWithTfi = CanopusTrendsMonthlyEntry & { avgTfi: number | null };

interface MiniChartProps {
  data: MonthlyEntryWithTfi[];
  dataKey: MonthlyMetricKey;
  color: string;
  label: string;
  unit?: string;
  height?: number;
  changeMode?: 'reduction' | 'growth';
  tip?: string;
}

function getMonthlyValue(d: MonthlyEntryWithTfi, key: MonthlyMetricKey): number | null {
  const v = (d as unknown as Record<string, unknown>)[key];
  return typeof v === 'number' ? v : null;
}

// Minutes Saved is a strictly positive metric by name. Floor negatives at 0.
const capMinutesSaved = (v: number | null): number | null => (v == null ? null : Math.max(0, v));

// Mockup pattern: Variance Reduction is strictly non-negative.
// Negative values (current variance > baseline) are treated the same way as
// insufficient data — they fall through to the Building / -- state, never
// rendered as a number.
const formatReductionPct = (v: number | null, precision: number = 0): string | null => {
  if (v == null || v < 0) return null;
  return `${v.toFixed(precision)}%`;
};

function MiniChart({
  data,
  dataKey,
  color,
  label,
  unit = '',
  height = 80,
  changeMode = 'reduction',
  tip,
}: MiniChartProps) {
  const cardClass = `tr-card tr-card-flex${tip ? ' metric-tip' : ''}`;
  const numeric = data
    .map((d) => getMonthlyValue(d, dataKey))
    .filter((v): v is number => v !== null);
  if (numeric.length === 0) {
    return (
      <div className={cardClass}>
        <SectionLabel text={label} />
        <div className="tr-empty">No data</div>
        {tip && <span className="metric-tip-text">{tip}</span>}
      </div>
    );
  }
  const max = Math.max(...numeric);
  const min = Math.min(...numeric);
  const range = max - min || 1;
  const lastVal = getMonthlyValue(data[data.length - 1] as MonthlyEntryWithTfi, dataKey);
  const firstVal = numeric[0] ?? 0;
  const lastNumeric = numeric[numeric.length - 1] ?? 0;
  let changeLabel = '';
  if (firstVal > 0) {
    if (changeMode === 'growth') {
      const raw = (lastNumeric / firstVal - 1) * 100;
      const pct = Math.abs(raw).toFixed(0);
      changeLabel = `${pct}% ${raw < 0 ? 'decline' : 'growth'}`;
    } else {
      // Reduction mode: positive when last < first (improvement). Flip the
      // sign and label it as an "increase" when the metric actually got
      // worse, so the suffix stays human-readable in both directions.
      const raw = (1 - lastNumeric / firstVal) * 100;
      const pct = Math.abs(raw).toFixed(0);
      changeLabel = `${pct}% ${raw < 0 ? 'increase' : 'reduction'}`;
    }
  }
  return (
    <div className={cardClass}>
      <div className="tr-mini-head">
        <SectionLabel text={label} />
        {/* Dynamic per-chart accent color */}
        <span className="tr-mini-value" style={{ color }}>
          {lastVal != null ? `${lastVal}${unit}` : '--'}
        </span>
      </div>
      <div className="tr-mini-bars" style={{ height }}>
        {data.map((d, i) => {
          const v = getMonthlyValue(d, dataKey);
          const isLast = i === data.length - 1;
          const pct = v != null ? ((v - min) / range) * 0.8 + 0.2 : 0;
          return (
            <div key={i} className="tr-mini-col">
              <div
                className="tr-mini-bar"
                style={{
                  // Dynamic: chart-specific color/height/min-height per bar
                  height: v != null ? height * pct : 0,
                  background: isLast ? color : `${color}66`,
                  minHeight: v != null ? 4 : 0,
                }}
              />
              <span className="tr-mini-month">{d.monthLabel.slice(0, 3)}</span>
            </div>
          );
        })}
      </div>
      <div className="tr-mini-foot">
        <span className="tr-mini-range">{data[0]?.monthLabel ?? ''}</span>
        {/* Dynamic: matches chart color */}
        <span className="tr-mini-change" style={{ color }}>
          {changeLabel}
        </span>
      </div>
      {tip && <span className="metric-tip-text">{tip}</span>}
    </div>
  );
}

function HeatMap({
  heatmap,
  roomLabel,
}: {
  heatmap: CanopusTrendsVarianceHeatmap;
  roomLabel: string;
}) {
  const flatVals: number[] = [];
  for (const row of heatmap.matrix) {
    for (const v of row) {
      if (v != null) flatVals.push(v);
    }
  }
  const hasData = flatVals.length > 0;
  const maxVal = hasData ? Math.max(...flatVals) : 0;
  const minVal = hasData ? Math.min(...flatVals) : 0;
  const range = maxVal - minVal || 1;
  const getColor = (val: number) => {
    const norm = (val - minVal) / range;
    if (norm < 0.33) return { bg: 'rgba(15,183,209,0.15)', text: '#0FB7D1' };
    if (norm < 0.66) return { bg: 'rgba(251,191,36,0.15)', text: '#FBBF24' };
    return { bg: 'rgba(255,179,0,0.15)', text: '#FFB300' };
  };

  return (
    <div className="tr-card">
      <SectionLabel
        text={`Duration Variance Heat Map — By ${roomLabel} & Day of Week (Avg, Minutes)`}
      />
      <div className="tr-heat-note">
        Lower is better. Green = low variance, Amber = moderate, Orange = high.
      </div>
      {!hasData || heatmap.rooms.length === 0 || heatmap.days.length === 0 ? (
        <div className="tr-empty">No heatmap data available</div>
      ) : (
        <table className="tr-heat-table">
          <thead>
            <tr>
              <th className="tr-heat-th" />
              {heatmap.days.map((day) => (
                <th key={day} className="tr-heat-th tr-heat-th--center">
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {heatmap.rooms.map((roomName, roomIdx) => (
              <tr key={roomName}>
                <td className="tr-heat-day">{roomName}</td>
                {heatmap.days.map((_, dayIdx) => {
                  const row = heatmap.matrix[dayIdx];
                  const val = row ? row[roomIdx] : null;
                  if (val == null) {
                    return (
                      <td key={dayIdx} className="tr-heat-td">
                        <div className="tr-heat-cell tr-heat-cell--empty">—</div>
                      </td>
                    );
                  }
                  const { bg, text } = getColor(val);
                  return (
                    <td key={dayIdx} className="tr-heat-td">
                      {/* Dynamic: bg/text colors interpolated by variance value */}
                      <div className="tr-heat-cell" style={{ background: bg, color: text }}>
                        {val}m
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TrendsSummary({
  trends,
  demoMode,
  roomLabel,
}: {
  trends: CanopusTrendsResult;
  // drives synthetic avgTfi injection for the Avg Team TFI chart.
  demoMode: boolean;
  /** UK facilities use 'OT' (Operating Theatre); everywhere else 'OR'. */
  roomLabel: string;
}) {
  // Inject a synthetic `avgTfi` per monthly entry when demo
  // mode is active. Values come from a shared helper (getDemoMonthlyTfiOverlay)
  // so the same adoption curve can be reused elsewhere. In live mode the
  // overlay is skipped — `avgTfi` is null and the Avg Team TFI MiniChart is
  // not rendered at all.
  // Apply the SI overlay first (replaces zero SI fields per
  // month with plausible values), then the TFI overlay (which adds the
  // synthetic avgTfi field). Order matters because getDemoMonthlyTfiOverlay
  // is generic over T and just spreads + augments — composing them is fine.
  const monthly: MonthlyEntryWithTfi[] = demoMode
    ? getDemoMonthlyTfiOverlay(getDemoMonthlySIOverlay(trends.monthlyTrends))
    : trends.monthlyTrends.map((m) => ({ ...m, avgTfi: null }));
  return (
    <div>
      <div className="tr-info-banner">
        All trends derived from reference guide check-off timestamps. Duration variance = actual
        case duration compared to empirical baseline for that surgeon-procedure combination.
      </div>
      {monthly.length === 0 ? (
        <div className="tr-empty-block">No monthly trend data available for this period.</div>
      ) : (
        <>
          <div className="tr-mini-row">
            <MiniChart
              data={monthly}
              dataKey="cases"
              color="#0FB7D1"
              label="Monthly Recorded Cases"
              changeMode="growth"
              tip="Total surgical cases recorded by Canopus for each bucket in the selected period."
            />
            <MiniChart
              data={monthly}
              dataKey="avgDurMin"
              color="#818CF8"
              label="Avg Case Duration (min)"
              unit="m"
              tip="Average actual case duration across all recorded cases in the bucket. Lower with more practice."
            />
            <MiniChart
              data={monthly}
              dataKey="avgVarianceMin"
              color="#FBBF24"
              label="Avg Duration Variance (min)"
              unit="m"
              tip="Average minutes a case ran over (or under) the historical baseline for that surgeon + procedure. Lower is better."
            />
          </div>
          <div className="tr-mini-row">
            <MiniChart
              data={monthly}
              dataKey="longRatePct"
              color="#FFB300"
              label="Cases Above Variance"
              unit="%"
              tip="Share of cases that ran >15% longer than the historical baseline for that surgeon + procedure."
            />
            <MiniChart
              data={monthly}
              dataKey="shortRatePct"
              color="#10B981"
              label="Cases Below Variance"
              unit="%"
              tip="Share of cases that ran >15% shorter than the historical baseline for that surgeon + procedure."
            />
            <MiniChart
              data={monthly}
              dataKey="runtimeAboveMin"
              color="#F97316"
              label="Runtimes Above Variance"
              unit="m"
              tip="Average minutes by which over-baseline cases exceeded their historical baseline."
            />
            <MiniChart
              data={monthly}
              dataKey="runtimeBelowMin"
              color="#34D399"
              label="Runtimes Below Variance"
              unit="m"
              tip="Average minutes by which under-baseline cases finished ahead of their historical baseline."
            />
          </div>
          <div className="tr-mini-row">
            <MiniChart
              data={monthly}
              dataKey="siTotal"
              color="#EC4899"
              label="Surgical Inquiries Delivered"
              changeMode="growth"
              tip="Total Surgical Inquiries (SI) — discrete knowledge retrievals via eXeX — delivered in the bucket."
            />
            <MiniChart
              data={monthly}
              dataKey="siOffCase"
              color="#8B5CF6"
              label="Off-Case SIs"
              changeMode="growth"
              tip="SIs delivered outside of an active surgical case (prep, review, training)."
            />
            <MiniChart
              data={monthly}
              dataKey="siPerCase"
              color="#A855F7"
              label="SI Density (per case)"
              unit="/case"
              changeMode="growth"
              tip="Average number of Surgical Inquiries served per recorded case. Higher means deeper in-case usage."
            />
            {/* Avg Team TFI MiniChart — rendered in both
                modes. In demo mode it's populated by getDemoMonthlyTfiOverlay
                above; in live mode every avgTfi is null and the chart shows
                the empty/-- state. TFI is not yet computed server-side. */}
            <MiniChart
              data={monthly}
              dataKey="avgTfi"
              color="#6366F1"
              label="Avg Team TFI"
              changeMode="growth"
              tip="Average Team Familiarity Index across cases in the bucket. Higher means the team has worked together more."
            />
          </div>
        </>
      )}
      {/* Duration Variance Trend by Specialty — commented out pending data cleanup.
      <div className="tr-card tr-card--mb">
        <SectionLabel text="Duration Variance Trend by Specialty" />
        {specialties.length === 0 ? (
          <div className="tr-empty">No specialty variance data available</div>
        ) : (
          <div className="tr-spec-grid">
            {specialties.map((s, i) => {
              const reductionPct = Math.max(0, s.reductionPct ?? 0);
              const reductionText = formatReductionPct(s.reductionPct ?? null, 0);
              return (
                <div key={i} className="tr-spec-card">
                  <div className="tr-spec-name">{s.specialty}</div>
                  <div className="tr-spec-row">
                    <span className="tr-spec-label">Baseline (first window)</span>
                    <span className="tr-spec-val">
                      {s.baselineVarMin != null ? `${s.baselineVarMin}m` : '--'}
                    </span>
                  </div>
                  <div className="tr-spec-row">
                    <span className="tr-spec-label">Current (latest window)</span>
                    <span className="tr-spec-val tr-spec-val--cur">
                      {s.currentVarMin != null ? `${s.currentVarMin}m` : '--'}
                    </span>
                  </div>
                  <div className="tr-spec-bar-wrap">
                    <Bar pct={Math.max(0, 100 - reductionPct)} color="#0FB7D1" h={6} />
                  </div>
                  <div className="tr-spec-foot">
                    <span className="tr-spec-reduction">
                      {reductionText != null ? reductionText : '--'}
                    </span>
                    <span className="tr-spec-cases">{s.cases} cases</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      */}
      <HeatMap heatmap={trends.varianceHeatmap} roomLabel={roomLabel} />
    </div>
  );
}

function VarianceOverview({
  monthly,
  specialties,
}: {
  monthly: CanopusTrendsMonthlyEntry[];
  specialties: CanopusTrendsSpecialtyVariance[];
}) {
  const monthsWithVariance = monthly.filter((m) => m.avgVarianceMin != null);
  const firstMonth = monthsWithVariance[0];
  const lastMonth = monthsWithVariance[monthsWithVariance.length - 1];
  const facilityBaseline = firstMonth?.avgVarianceMin ?? null;
  const facilityCurrent = lastMonth?.avgVarianceMin ?? null;
  const lastMonthCases = lastMonth?.cases ?? 0;
  const facilityReductionRaw =
    facilityBaseline != null && facilityBaseline > 0 && facilityCurrent != null
      ? (1 - facilityCurrent / facilityBaseline) * 100
      : null;
  const facilityReductionText = formatReductionPct(facilityReductionRaw, 1);
  const facilityMinsSavedRaw =
    facilityBaseline != null && facilityCurrent != null
      ? (facilityBaseline - facilityCurrent) * lastMonthCases
      : null;
  const facilityMinsSavedCapped = capMinutesSaved(facilityMinsSavedRaw);
  const facilityMinsSaved =
    facilityMinsSavedCapped != null ? facilityMinsSavedCapped.toFixed(0) : null;
  const chartMax =
    facilityBaseline != null
      ? Math.max(facilityBaseline, ...monthsWithVariance.map((m) => m.avgVarianceMin as number))
      : 0;

  return (
    <React.Fragment>
      <div className="tr-kpi-row">
        <div className="tr-kpi-tile tr-kpi-tile--accent">
          <div className="tr-kpi-label">Variance</div>
          <div className="tr-kpi-big">
            {facilityReductionText != null ? facilityReductionText : '--'}
          </div>
          <div className="tr-kpi-sub">
            Reduction from baseline · {facilityBaseline != null ? `+${facilityBaseline}m` : '--'} →{' '}
            {facilityCurrent != null ? `+${facilityCurrent}m` : '--'}
          </div>
        </div>
        <div className="tr-kpi-tile">
          <div className="tr-kpi-label">Minutes Saved (Latest Month)</div>
          <div className="tr-kpi-big">{facilityMinsSaved != null ? facilityMinsSaved : '--'}</div>
          <div className="tr-kpi-sub">Across {lastMonthCases} recorded cases</div>
        </div>
        <div className="tr-kpi-tile">
          <div className="tr-kpi-label">Avg Variance Per Case</div>
          <div className="tr-kpi-big tr-kpi-big--warn">
            {facilityCurrent != null ? `+${facilityCurrent}m` : '--'}
          </div>
          <div className="tr-kpi-sub">Latest month average</div>
        </div>
      </div>
      <div className="tr-card tr-card--mb">
        <SectionLabel text="Variance Over Time (Facility-Wide)" />
        {monthly.length === 0 || facilityBaseline == null || facilityBaseline <= 0 ? (
          <div className="tr-empty">No facility-wide variance data</div>
        ) : (
          <>
            <div className="tr-vot-bars">
              {monthly.map((d, i) => {
                const isLast = i === monthly.length - 1;
                const v = d.avgVarianceMin;
                const pct = v != null && chartMax > 0 ? v / chartMax : 0;
                return (
                  <div key={i} className="tr-vot-col">
                    <span className={`tr-vot-val ${isLast ? 'tr-vot-val--last' : ''}`.trim()}>
                      {v != null ? `+${v}m` : '--'}
                    </span>
                    <div
                      className={`tr-vot-bar ${isLast ? 'tr-vot-bar--last' : 'tr-vot-bar--prior'}`}
                      style={{
                        // Dynamic: per-month bar height
                        height: v != null ? 120 * pct : 0,
                        minHeight: v != null ? 8 : 0,
                      }}
                    />
                    <span className="tr-vot-month">{d.monthLabel.slice(0, 3)}</span>
                  </div>
                );
              })}
            </div>
            <div className="tr-vot-foot">
              <span className="tr-vot-foot-left">
                Baseline period ({firstMonth?.monthLabel ?? '--'})
              </span>
              <span className="tr-vot-foot-right">
                {facilityReductionText != null ? `Reduction: ${facilityReductionText}` : ''}
              </span>
            </div>
          </>
        )}
      </div>
      <div className="tr-card">
        <SectionLabel text="Variance by Specialty" />
        {specialties.length === 0 ? (
          <div className="tr-empty">No specialty data</div>
        ) : (
          specialties.map((s, i) => {
            const reductionPct = Math.max(0, s.reductionPct ?? 0);
            const reductionText = formatReductionPct(s.reductionPct ?? null, 0);
            const isLast = i === specialties.length - 1;
            return (
              <div
                key={i}
                className={`tr-specrow ${!isLast ? 'tr-specrow--with-border' : ''}`.trim()}
              >
                <span className="tr-specrow-name">{s.specialty}</span>
                <div className="tr-specrow-mid">
                  <div className="tr-specrow-mid-head">
                    <span className="tr-specrow-mid-label">
                      Baseline: {s.baselineVarMin != null ? `+${s.baselineVarMin}m` : '--'}
                    </span>
                    <span className="tr-specrow-mid-cur">
                      Current: {s.currentVarMin != null ? `+${s.currentVarMin}m` : '--'}
                    </span>
                  </div>
                  <div className="tr-specrow-track">
                    <div
                      className="tr-specrow-fill"
                      style={{
                        // Dynamic: width is per-row reduction percentage
                        width: `${Math.max(0, (1 - reductionPct / 100) * 100)}%`,
                      }}
                    />
                    <div
                      className="tr-specrow-rest"
                      style={{
                        // Dynamic: position + width follow reduction
                        left: `${Math.max(0, (1 - reductionPct / 100) * 100)}%`,
                        width: `${Math.max(0, reductionPct)}%`,
                      }}
                    />
                  </div>
                </div>
                <span className="tr-specrow-pct">
                  {reductionText != null ? reductionText : '--'}
                </span>
                <span className="tr-specrow-cases">{s.cases} cases</span>
              </div>
            );
          })
        )}
      </div>
    </React.Fragment>
  );
}

function VarianceBySurgeon({ surgeons }: { surgeons: CanopusTrendsSurgeonProcedureVariance[] }) {
  return (
    <div className="tr-card">
      <SectionLabel text="Duration Variance by Surgeon + Procedure" />
      <div className="tr-surg-note">
        Baseline = avg duration variance from first 15 recorded cases. Current = latest rolling
        window. Minimum 15 cases required for baseline comparison.
      </div>
      {surgeons.length === 0 ? (
        <div className="tr-empty">No surgeon variance data</div>
      ) : (
        <table className="tr-table">
          <thead>
            <tr>
              {[
                'Surgeon',
                'Procedure',
                'Baseline Variance',
                'Current Variance',
                'Reduction',
                'Total Cases',
                'Trend',
              ].map((h) => (
                <th key={h} className="tr-th">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {surgeons.map((s, i) => {
              const building = s.trend === 'building';
              const trendColor =
                s.trend === 'improving' ? '#0FB7D1' : s.trend === 'stable' ? '#FBBF24' : '#6366F1';
              const trendIcon = s.trend === 'improving' ? '✓' : s.trend === 'stable' ? '↔' : '○';
              const reductionText = building ? null : formatReductionPct(s.reductionPct ?? null, 0);
              return (
                <tr key={i} className={`tr-row ${building ? 'tr-row--building' : ''}`.trim()}>
                  <td className="tr-td tr-td--surgeon">{s.surgeonName}</td>
                  <td className="tr-td tr-td--small">{s.procedureName}</td>
                  <td className="tr-td">
                    {s.baselineVarMin != null ? `+${s.baselineVarMin}m` : '--'}
                  </td>
                  <td className="tr-td tr-td--cur">
                    {s.currentVarMin != null ? `+${s.currentVarMin}m` : '--'}
                  </td>
                  <td className={`tr-td ${building ? 'tr-td--bold-warn' : 'tr-td--bold-accent'}`}>
                    {building ? 'Building' : reductionText != null ? reductionText : '--'}
                    {building && <span className="tr-building-cases"> ({s.totalCases}/15)</span>}
                  </td>
                  <td className="tr-td">{s.totalCases}</td>
                  {/* Dynamic: trend color picked from {improving|stable|building} */}
                  <td className="tr-td-trend" style={{ color: trendColor }}>
                    {trendIcon} {s.trend.charAt(0).toUpperCase() + s.trend.slice(1)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function VarianceTimeline({
  monthly,
  adoption,
}: {
  monthly: CanopusTrendsMonthlyEntry[];
  adoption: CanopusTrendsAdoptionBucket[];
}) {
  const monthsWithVariance = monthly.filter((m) => m.avgVarianceMin != null);
  const firstMonth = monthsWithVariance[0];
  const facilityBaseline = firstMonth?.avgVarianceMin ?? null;

  const adoptionVals = adoption.map((a) => a.avgVarianceMin).filter((v): v is number => v != null);
  const adoptionMax = adoptionVals.length > 0 ? Math.max(...adoptionVals) : 0;

  return (
    <React.Fragment>
      <div className="tr-card tr-card--mb">
        <SectionLabel text="Adoption Curve — Duration Variance Decreases as Teams Learn eXeX" />
        <div className="tr-adopt-note">
          Average duration variance per case, shown by case number bucket (nth recorded case for a
          surgeon+procedure combo). Demonstrates the natural learning curve effect.
        </div>
        {adoption.length <= 3 || adoptionMax <= 0 ? (
          <div className="tr-empty">
            Adoption curve will appear once teams accumulate post-baseline cases (16+ per surgeon +
            procedure).
          </div>
        ) : (
          <>
            <div className="tr-adopt-bars">
              {adoption.map((d, i) => {
                const v = d.avgVarianceMin;
                const pct = v != null ? v / adoptionMax : 0;
                const isBaseline = i < 3;
                return (
                  <div key={i} className="tr-adopt-col">
                    <span className="tr-adopt-val">{v != null ? `+${v}m` : '--'}</span>
                    <div
                      className={`tr-adopt-bar ${isBaseline ? 'tr-adopt-bar--baseline' : 'tr-adopt-bar--post'}`}
                      style={{
                        // Dynamic: bucket height
                        height: v != null ? 100 * pct : 0,
                        minHeight: v != null ? 6 : 0,
                      }}
                    />
                    <span className="tr-adopt-bucket">{d.bucket}</span>
                  </div>
                );
              })}
            </div>
            <div className="tr-adopt-legend">
              <div className="tr-adopt-legend-item">
                <div className="tr-adopt-legend-swatch tr-adopt-legend-swatch--baseline" />
                <span className="tr-adopt-legend-text">Baseline period (cases 1-15)</span>
              </div>
              <div className="tr-adopt-legend-item">
                <div className="tr-adopt-legend-swatch tr-adopt-legend-swatch--post" />
                <span className="tr-adopt-legend-text">Post-baseline (improvement measured)</span>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="tr-card">
        <SectionLabel text="Month-Over-Month Variance Progression" />
        {monthly.length === 0 ? (
          <div className="tr-empty">No monthly data</div>
        ) : (
          <table className="tr-table">
            <thead>
              <tr>
                {[
                  'Month',
                  'Cases',
                  'Avg Variance',
                  'vs Baseline',
                  'vs Prior Month',
                  'Cumulative Reduction',
                ].map((h) => (
                  <th key={h} className="tr-th">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthly.map((m, i) => {
                const isFirst = i === 0;
                const cur = m.avgVarianceMin;
                const prior = i > 0 ? (monthly[i - 1]?.avgVarianceMin ?? null) : null;
                const vsBaselineRaw =
                  facilityBaseline != null && facilityBaseline > 0 && cur != null
                    ? (1 - cur / facilityBaseline) * 100
                    : null;
                const vsPriorRaw =
                  prior != null && prior > 0 && cur != null ? (1 - cur / prior) * 100 : null;
                const vsBaselineText = formatReductionPct(vsBaselineRaw, 1);
                const vsPriorText = formatReductionPct(vsPriorRaw, 1);
                return (
                  <tr key={i} className={`tr-row ${isFirst ? 'tr-row--first' : ''}`.trim()}>
                    <td className="tr-td-7 tr-td--month">
                      {m.monthLabel}
                      {isFirst && <span className="tr-baseline-tag">BASELINE</span>}
                    </td>
                    <td className="tr-td-7">{m.cases}</td>
                    <td className="tr-td-7 tr-td--cur-warn">{cur != null ? `+${cur}m` : '--'}</td>
                    <td className={`tr-td-7 ${isFirst ? 'tr-td--muted' : 'tr-td--cur'}`}>
                      {isFirst ? '--' : vsBaselineText != null ? vsBaselineText : '--'}
                    </td>
                    <td
                      className={`tr-td-7 tr-td-7--small ${vsPriorText != null ? 'tr-td--cur' : 'tr-td--muted'}`}
                    >
                      {vsPriorText != null ? vsPriorText : '--'}
                    </td>
                    <td className={`tr-td-7 ${isFirst ? 'tr-td--muted' : 'tr-td--bold-accent'}`}>
                      {isFirst ? '0%' : vsBaselineText != null ? vsBaselineText : '--'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </React.Fragment>
  );
}

function VarianceAnalysisTab({ trends }: { trends: CanopusTrendsResult }) {
  const [view, setView] = useState<VarianceView>('overview');

  return (
    <div>
      <div className="tr-meth">
        <div className="tr-meth-title">Duration Variance Methodology</div>
        <div className="tr-meth-body">
          <strong className="tr-meth-strong">Definition:</strong> Duration variance = actual case
          duration minus the empirical baseline duration for that surgeon + procedure combination.
          Positive variance means the case ran longer than baseline; negative means faster. Measured
          entirely from reference guide check-off timestamps.
        </div>
        <div className="tr-meth-body tr-meth-body--mt">
          <strong className="tr-meth-strong">Baseline:</strong> Empirical average derived from the
          first 15+ recorded cases for each surgeon + procedure combination. This establishes the
          pattern starting point.
        </div>
        <div className="tr-meth-body tr-meth-body--mt">
          <strong className="tr-meth-strong">Comparison:</strong> Current variance compared against
          rolling windows. Always eXeX time vs. eXeX time — never mixed with hospital timekeeping.
        </div>
        <div className="tr-meth-body tr-meth-body--mt">
          <strong className="tr-meth-strong">Minimum data:</strong> 15+ completed cases per surgeon
          + procedure before publishing a baseline. Cases below threshold show raw duration only.
        </div>
        <div className="tr-meth-note">
          Note: Variance is neutral — it measures deviation from the surgeon&apos;s own empirical
          pattern, not an external standard. A higher variance does not inherently indicate a
          problem; it flags cases worth reviewing. Hospital time and eXeX recording time use
          different methods and are not compared.
        </div>
      </div>
      <div className="tr-subtabs">
        {(
          [
            { id: 'overview', label: 'Facility Overview' },
            { id: 'surgeon', label: 'By Surgeon' },
            { id: 'timeline', label: 'Timeline' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            className={`tr-subtab ${view === t.id ? 'tr-subtab--active' : ''}`.trim()}
          >
            {t.label}
          </button>
        ))}
      </div>
      {view === 'overview' && (
        <VarianceOverview monthly={trends.monthlyTrends} specialties={trends.specialtyVariance} />
      )}
      {view === 'surgeon' && <VarianceBySurgeon surgeons={trends.surgeonProcedureVariance} />}
      {view === 'timeline' && (
        <VarianceTimeline monthly={trends.monthlyTrends} adoption={trends.adoptionCurve} />
      )}
    </div>
  );
}

function KpiTile({
  label,
  value,
  color = '#0FB7D1',
  sub,
}: {
  label: string;
  value: string | number;
  color?: string;
  sub?: string;
}) {
  return (
    <div className="tr-kpi-mini">
      <div className="tr-kpi-mini-label">{label}</div>
      {/* Dynamic: per-tile accent color */}
      <div className="tr-kpi-mini-value" style={{ color }}>
        {value}
      </div>
      {sub && <div className="tr-kpi-mini-sub">{sub}</div>}
    </div>
  );
}

function DurationIntelligenceTab({
  kpis,
  surgeons,
  rooms,
}: {
  kpis: CanopusTrendsKpis;
  surgeons: CanopusTrendsSurgeonDurationIntel[];
  rooms: CanopusTrendsRoomGap[];
}) {
  const fmtNum = (v: number | null): string => (v != null ? `${v}` : '--');
  return (
    <div>
      <div className="tr-kpi-row">
        <KpiTile label="Total Recorded Cases" value={kpis.totalRecordedCases.toLocaleString()} />
        <KpiTile
          label="Avg Duration"
          value={kpis.avgDurationMin != null ? `${kpis.avgDurationMin}m` : '--'}
          color="#818CF8"
        />
        <KpiTile label="Profiles" value={kpis.profilesCount.toLocaleString()} color="#A855F7" />
        <KpiTile label="Surgeons" value={kpis.surgeonsCount.toLocaleString()} color="#EC4899" />
      </div>

      <div className="tr-card tr-card--mb">
        <SectionLabel text="Surgeon Duration Intelligence" />
        {surgeons.length === 0 ? (
          <div className="tr-empty">No surgeon duration data</div>
        ) : (
          <table className="tr-table">
            <thead>
              <tr>
                {[
                  'Surgeon',
                  'Top Procedure',
                  'Cases',
                  'Avg Dur',
                  'Longest Phase',
                  'Running Long %',
                  'Avg Pre-Op Setup',
                ].map((h) => (
                  <th key={h} className="tr-th">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {surgeons.map((s, i) => (
                <tr key={i} className="tr-row">
                  <td className="tr-td tr-td--surgeon">{s.surgeonName}</td>
                  <td className="tr-td tr-td--small">{s.topProcedure ?? '--'}</td>
                  <td className="tr-td">{s.cases}</td>
                  <td className="tr-td tr-td--month">
                    {s.avgDurMin != null ? `${s.avgDurMin}m` : '--'}
                  </td>
                  <td className="tr-td tr-td--small">
                    {s.longestPhaseName ?? '--'}
                    {s.longestPhaseAvgMin != null && (
                      <span className="tr-td--muted"> · {s.longestPhaseAvgMin}m</span>
                    )}
                  </td>
                  <td
                    className={`tr-td ${
                      s.runningLongPct != null && s.runningLongPct > 25
                        ? 'tr-td--running-long'
                        : 'tr-td--running-ok'
                    }`}
                  >
                    {s.runningLongPct != null ? `${s.runningLongPct}%` : '--'}
                  </td>
                  <td className="tr-td">
                    {s.avgPreOpSetupMin != null ? `${s.avgPreOpSetupMin}m` : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="tr-card">
        <SectionLabel text="Room Gap Times" />
        <div className="tr-surg-note">
          Time between cases in each room — measured between the end of one recording and the start
          of the next.
        </div>
        {rooms.length === 0 ? (
          <div className="tr-empty">No room gap data</div>
        ) : (
          <div className="tr-room-grid">
            {rooms.map((r, i) => (
              <div key={i} className="tr-room-card">
                <div className="tr-room-name">{r.roomName}</div>
                <div className="tr-room-row">
                  <span className="tr-room-row-label">Last Gap</span>
                  <span className="tr-room-row-val">
                    {r.lastGapMin != null ? `${r.lastGapMin}m` : '--'}
                  </span>
                </div>
                <div className="tr-room-row">
                  <span className="tr-room-row-label">Avg Gap</span>
                  <span className="tr-room-row-val tr-room-row-val--muted">
                    {r.avgGapMin != null ? `${r.avgGapMin}m` : '--'}
                  </span>
                </div>
                <div className="tr-room-row tr-room-row--last">
                  <span className="tr-room-row-label">Gaps Measured</span>
                  <span className="tr-room-row-val tr-room-row-val--muted">
                    {fmtNum(r.gapsMeasured)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function TrendsTab({
  facilityId,
  /** UK facilities use 'OT' (Operating Theatre); everywhere else 'OR'. */
  roomLabel = 'OR',
  // gates the synthetic Avg Team TFI MiniChart and the
  // fully synthesized trends payload (real fetch is bypassed entirely).
  demoMode = false,
  // real surgeon pool used to label demo trends rows.
  // Optional; falls back to "Dr. Demo N" placeholder names when null/empty.
  realSurgeons = null,
}: {
  facilityId: string | null;
  roomLabel?: string;
  demoMode?: boolean;
  realSurgeons?: InsightsSurgeon[] | null;
}) {
  const view = 'trends' as TrendsView;
  const [period, setPeriod] = useState<CanopusSIPeriod>('90d');
  const [trends, setTrends] = useState<CanopusTrendsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!facilityId) {
      setTrends(null);
      return;
    }
    // completely bypass the real fetch — synthesize a
    // full trends payload locally. Avoids any sparse / null cells the
    // real data leaves in MiniCharts, the variance heatmap, etc.
    if (demoMode) {
      setLoading(false);
      setError(null);
      setTrends(getDemoTrends(period, realSurgeons));
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    canopusCommandService
      .getTrends(facilityId, period)
      .then((data) => {
        if (cancelled) return;
        setTrends(data);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load trends data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId, period, demoMode, realSurgeons]);

  const periods: Array<{ id: CanopusSIPeriod; label: string }> = [
    { id: '7d', label: '7 Days' },
    { id: '90d', label: '90 Days' },
    { id: 'ytd', label: 'YTD' },
  ];

  const isEmpty =
    trends != null &&
    trends.monthlyTrends.length === 0 &&
    trends.specialtyVariance.length === 0 &&
    trends.surgeonProcedureVariance.length === 0 &&
    trends.surgeonDurationIntel.length === 0 &&
    trends.roomGapTimes.length === 0 &&
    trends.adoptionCurve.length === 0 &&
    trends.varianceHeatmap.matrix.length === 0;

  return (
    <div>
      {/* Period picker + sub-view switcher */}
      <div className="tr-toolbar">
        <div className="tr-period-group">
          {periods.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`tr-period-btn ${period === p.id ? 'tr-period-btn--active' : ''}`.trim()}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {!facilityId && <div className="tr-state tr-state--muted">No facility selected</div>}

      {facilityId && loading && !trends && (
        <div className="tr-skel-grid">
          <div className="tr-skel-row">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="tr-skel-tile">
                <div className="loading-blur tr-skel-label">METRIC LABEL</div>
                <div className="loading-blur tr-skel-value">000</div>
              </div>
            ))}
          </div>
          <div className="tr-skel-chart">
            <div className="loading-blur tr-skel-title">Chart Title</div>
            <div className="loading-blur tr-skel-chart-body" />
          </div>
        </div>
      )}

      {facilityId && !loading && error && <div className="tr-state tr-state--warn">{error}</div>}

      {facilityId && !loading && !error && trends && isEmpty && (
        <div className="tr-state tr-state--muted">No data available for this period</div>
      )}

      {facilityId && !error && trends && !isEmpty && (
        <div className={loading ? 'loading-blur' : undefined}>
          {view === 'trends' && (
            <TrendsSummary trends={trends} demoMode={demoMode} roomLabel={roomLabel} />
          )}
          {view === 'variance' && <VarianceAnalysisTab trends={trends} />}
          {view === 'duration' && (
            <DurationIntelligenceTab
              kpis={trends.kpis}
              surgeons={trends.surgeonDurationIntel}
              rooms={trends.roomGapTimes}
            />
          )}
        </div>
      )}
    </div>
  );
}
